import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, UserPlus, CreditCard, Send, Search, Copy, Check, Loader2, ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface SearchedUser {
  user_id: string;
  email: string;
  full_name: string | null;
  announcement_credits: number | null;
  ai_assist_uses_remaining: number | null;
  has_purchased_bundle: boolean | null;
  highest_bundle_purchased: string | null;
  referral_code: string | null;
  influencer_access: boolean | null;
  created_at: string;
}

export default function AdminManagement() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }).then(({ data }) => {
      setIsAdmin(!!data);
    });
  }, [user?.id]);

  if (isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center gap-4 px-4">
          <Link to="/admin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Admin Hub</span>
          </Link>
          <h1 className="text-base sm:text-lg font-bold text-foreground">Management Tools</h1>
        </div>
      </header>

      <main className="container py-6 max-w-4xl px-4">
        <div className="mb-4 rounded-md border border-primary/20 bg-primary/5 p-3 text-xs sm:text-sm text-muted-foreground">
          To change an existing member's plan or credits, use{' '}
          <Link to="/admin/members" className="text-primary font-medium underline">All Members → Upgrade Existing Member</Link>.
        </div>
        <Tabs defaultValue="onboard" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
            <TabsTrigger value="onboard" className="flex items-center gap-1.5 text-xs sm:text-sm py-2">
              <UserPlus className="h-4 w-4" /> <span className="truncate">New Influencer</span>
            </TabsTrigger>
            <TabsTrigger value="credits" className="flex items-center gap-1.5 text-xs sm:text-sm py-2">
              <CreditCard className="h-4 w-4" /> <span className="truncate">Credits</span>
            </TabsTrigger>
            <TabsTrigger value="affirmations" className="flex items-center gap-1.5 text-xs sm:text-sm py-2">
              <ImageIcon className="h-4 w-4" /> <span className="truncate">Affirmations</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-1.5 text-xs sm:text-sm py-2">
              <Send className="h-4 w-4" /> <span className="truncate">Messages</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="onboard">
            <InfluencerOnboardTab />
          </TabsContent>
          <TabsContent value="credits">
            <CreditManagementTab />
          </TabsContent>
          <TabsContent value="affirmations">
            <AffirmationGrantTab />
          </TabsContent>
          <TabsContent value="messages">
            <PushMessagesTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ─── Influencer Onboarding ─── */
function InfluencerOnboardTab() {
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [tier, setTier] = useState('legacy');
  const [credits, setCredits] = useState('25');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ referral_link?: string; magic_link?: string; tracking_code?: string; user_id?: string; success?: boolean; email_sent?: boolean; email_error?: string | null; email_delivery_mode?: string; manual_share_text?: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);

  const handleInvite = async () => {
    if (!email.trim()) { toast.error('Email is required'); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: {
          action: 'invite_influencer',
          email: email.trim(),
          plan_tier: tier,
          credits: parseInt(credits),
          referral_label: label.trim() || undefined,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setResult(data);
      setEmailSentTo(email.trim());
      if (data?.email_sent) {
        if (data?.email_delivery_mode === 'fallback_resend_domain') {
          toast.success(`✅ Influencer onboarded! Invite delivered to ${email.trim()} via backup sender.`);
        } else {
          toast.success(`✅ Influencer onboarded! Email delivered to ${email.trim()}`);
        }
      } else {
        toast.warning(`⚠️ Influencer created but email failed: ${data?.email_error || 'Unknown error'}. Use the magic link below to share manually.`);
      }
    } catch (err: any) {
      console.error('[AdminOnboard] Error:', err);
      toast.error(err.message || 'Failed to invite influencer');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-primary" />
          New Influencer Onboarding
        </CardTitle>
        <CardDescription>
          Use this only when creating influencer access or sending a first-time magic link. To upgrade an existing member,
          use <Link to="/admin/members" className="text-primary underline">Upgrade Existing Member</Link>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Influencer Email *</label>
            <Input
              placeholder="influencer@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Display Name / Label</label>
            <Input
              placeholder="e.g. EvelynWhite"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Plan Tier</label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="starter">Starter (5 credits)</SelectItem>
                <SelectItem value="connection">Connection (10 credits)</SelectItem>
                <SelectItem value="legacy">Legacy (25 credits)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Credits Override</label>
            <Input
              type="number"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              min="1"
            />
          </div>
        </div>

        <Button onClick={handleInvite} disabled={loading} className="w-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
          Send Magic Link & Set Up Access
        </Button>

        {result && (
          <div className="mt-4 space-y-3 p-4 bg-primary/5 rounded-lg border-2 border-primary/30">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Check className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">
                  {result.email_sent
                    ? result.email_delivery_mode === 'fallback_resend_domain'
                      ? '✅ Onboarded — Invite Sent via Backup Sender'
                      : '✅ Influencer Successfully Onboarded'
                    : '⚠️ Onboarded — Email Not Delivered'}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {result.email_sent 
                    ? <>
                        Email sent to <strong>{emailSentTo}</strong>
                        {result.email_delivery_mode === 'fallback_resend_domain' ? ' using backup delivery.' : ''}
                      </>
                    : <>Email to <strong>{emailSentTo}</strong> failed: {result.email_error || 'Unknown error'}. Share the magic link below manually.</>
                  }
                </p>
              </div>
            </div>

            {result.tracking_code && (
              <div className="text-xs text-muted-foreground">
                Tracking code: <Badge variant="secondary">{result.tracking_code}</Badge>
              </div>
            )}
            
            {result.referral_link && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Referral Link (for tracking)</p>
                <div className="flex gap-2">
                  <Input value={result.referral_link} readOnly className="text-xs" />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(result.referral_link!, 'ref')}>
                    {copied === 'ref' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            )}

            {result.magic_link && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Magic Link (also emailed to influencer)</p>
                <div className="flex gap-2">
                  <Input value={result.magic_link} readOnly className="text-xs" />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(result.magic_link!, 'magic')}>
                    {copied === 'magic' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            )}

            {result.manual_share_text && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Manual Share Message (send via SMS/DM/email)</p>
                <div className="space-y-2">
                  <Textarea value={result.manual_share_text} readOnly className="min-h-[120px] text-xs" />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(result.manual_share_text!, 'share')}>
                    {copied === 'share' ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                    Copy Share Message
                  </Button>
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground italic">
              The influencer will also appear in the Influencer Analytics dashboard once their code is active.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Credit Management ─── */
function CreditManagementTab() {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<SearchedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<SearchedUser | null>(null);
  const [editCredits, setEditCredits] = useState('');
  const [editTier, setEditTier] = useState('');
  const [saving, setSaving] = useState(false);

  const searchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_search_users', { p_search: search.trim() });
    if (error) {
      toast.error('Search failed');
      console.error(error);
    } else {
      setUsers((data as unknown as SearchedUser[]) || []);
    }
    setLoading(false);
  };

  const saveCredits = async () => {
    if (!editingUser) return;
    setSaving(true);
    const { error } = await supabase.rpc('admin_update_credits', {
      p_target_user_id: editingUser.user_id,
      p_announcement_credits: editCredits ? parseInt(editCredits) : null,
      p_has_purchased_bundle: editTier ? true : null,
      p_highest_bundle: editTier || null,
    });
    if (error) {
      toast.error('Failed to update credits');
    } else {
      toast.success('Credits updated');
      setEditingUser(null);
      searchUsers();
    }
    setSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          User Credit Management
        </CardTitle>
        <CardDescription>Search users by email or name to view and edit their credits.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
          />
          <Button onClick={searchUsers} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>

        {users.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Ref Code</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="text-sm font-medium">{u.email}</TableCell>
                    <TableCell className="text-sm">{u.full_name || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{u.announcement_credits ?? 0}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {u.has_purchased_bundle ? (
                        <Badge variant="default">{u.highest_bundle_purchased || 'Paid'}</Badge>
                      ) : (
                        <span className="text-muted-foreground">Free</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.referral_code || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingUser(u);
                          setEditCredits(String(u.announcement_credits ?? 0));
                          setEditTier(u.highest_bundle_purchased || '');
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Edit modal */}
        {editingUser && (
          <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border space-y-3">
            <h4 className="text-sm font-semibold">
              Editing: {editingUser.email}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Announcement Credits</label>
                <Input
                  type="number"
                  value={editCredits}
                  onChange={(e) => setEditCredits(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Plan Tier</label>
                <Select value={editTier} onValueChange={setEditTier}>
                  <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="connection">Connection</SelectItem>
                    <SelectItem value="legacy">Legacy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveCredits} disabled={saving} size="sm">
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Save Changes
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditingUser(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Push Messages ─── */
function PushMessagesTab() {
  const [targetType, setTargetType] = useState<'specific' | 'search'>('specific');
  const [targetEmail, setTargetEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchedUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SearchedUser[]>([]);

  const templates = [
    { label: 'Gentle Encouragement', subject: 'Someone would love to hear from you 💛', body: '<p>We noticed you haven\'t created a message in a while. Remember, the people in your life would love to know what you\'re thinking.</p><p>Even a simple "I\'m thinking of you" can mean the world.</p>' },
    { label: 'Influencer Welcome', subject: 'Welcome to Say It NOW — Let\'s make magic! ✨', body: '<p>We\'re so excited to have you on board! Your voice can inspire thousands of people to say what matters most.</p><p>Start by creating your first message, then share your unique link with your audience.</p>' },
    { label: 'Credits Running Low', subject: 'Your Say It NOW credits are running low 📝', body: '<p>It looks like you\'re running low on announcement credits. Don\'t worry — you can top up anytime to keep sharing meaningful messages.</p>' },
  ];

  const handleSearch = async () => {
    const { data } = await supabase.rpc('admin_search_users', { p_search: searchQuery });
    setSearchResults((data as unknown as SearchedUser[]) || []);
  };

  const sendMessage = async (email: string) => {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: {
          action: 'send_message',
          target_email: email,
          subject: subject || 'A message from Say It NOW 💛',
          message_html: messageBody,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Email sent to ${email}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const sendToAll = async () => {
    if (targetType === 'specific') {
      await sendMessage(targetEmail);
    } else {
      for (const u of selectedUsers) {
        await sendMessage(u.email);
      }
      toast.success(`Sent to ${selectedUsers.length} users`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5 text-primary" />
          Push Messages
        </CardTitle>
        <CardDescription>Send encouragement or reminder emails to users.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick templates */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Quick Templates</label>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <Button
                key={t.label}
                variant="outline"
                size="sm"
                onClick={() => { setSubject(t.subject); setMessageBody(t.body); }}
                className="text-xs"
              >
                {t.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Target selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Send To</label>
          <Select value={targetType} onValueChange={(v) => setTargetType(v as any)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="specific">Specific Email</SelectItem>
              <SelectItem value="search">Search & Select Users</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {targetType === 'specific' ? (
          <Input
            placeholder="user@example.com"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
          />
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button size="sm" onClick={handleSearch}><Search className="h-4 w-4" /></Button>
            </div>
            {searchResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-border rounded-md">
                {searchResults.map((u) => {
                  const isSelected = selectedUsers.some((s) => s.user_id === u.user_id);
                  return (
                    <button
                      key={u.user_id}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex justify-between ${isSelected ? 'bg-primary/10' : ''}`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedUsers((prev) => prev.filter((s) => s.user_id !== u.user_id));
                        } else {
                          setSelectedUsers((prev) => [...prev, u]);
                        }
                      }}
                    >
                      <span>{u.email}</span>
                      <span className="text-muted-foreground">{u.full_name || ''}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {selectedUsers.length > 0 && (
              <p className="text-xs text-muted-foreground">{selectedUsers.length} user(s) selected</p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Subject</label>
          <Input
            placeholder="Email subject line"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Message (HTML)</label>
          <Textarea
            placeholder="<p>Your message here...</p>"
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            rows={5}
          />
        </div>

        <Button
          onClick={sendToAll}
          disabled={sending || (!targetEmail && selectedUsers.length === 0)}
          className="w-full"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          Send Message
        </Button>
      </CardContent>
    </Card>
  );
}

/* ─── Affirmation Generation Grant ─── */
function AffirmationGrantTab() {
  const [email, setEmail] = useState('');
  const [count, setCount] = useState('1');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    new_count?: number;
    remaining?: number;
    cap?: number;
    plan_tier?: string;
  } | null>(null);

  const handleGrant = async () => {
    if (!email.trim()) { toast.error('Email is required'); return; }
    const num = parseInt(count);
    if (!num || num < 1) { toast.error('Count must be at least 1'); return; }

    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: {
          action: 'grant_affirmation_generations',
          email: email.trim(),
          additional_count: num,
        },
      });
      if (error) throw error;
      const res = data as unknown as {
        success: boolean;
        message: string;
        error?: string;
        new_count?: number;
        remaining?: number;
        cap?: number;
        plan_tier?: string;
      };
      if (!res.success) {
        toast.error(res.error || 'Failed');
        setResult({ success: false, message: res.error || 'User not found' });
      } else {
        toast.success(res.message);
        setResult({
          success: true,
          message: res.message,
          new_count: res.new_count,
          remaining: res.remaining,
          cap: res.cap,
          plan_tier: res.plan_tier,
        });
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to grant generations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-primary" />
          Affirmation Image Grants
        </CardTitle>
        <CardDescription>
          Grant additional affirmation image generations to a user by email. This restores available capacity against their current plan limit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">User Email *</label>
            <Input
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Additional Generations</label>
            <Input
              type="number"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              min="1"
            />
          </div>
        </div>

        <Button onClick={handleGrant} disabled={loading} className="w-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ImageIcon className="h-4 w-4 mr-2" />}
          Grant Generations
        </Button>

        {result && (
          <div className={`p-3 rounded-lg border ${result.success ? 'bg-primary/5 border-primary/30' : 'bg-destructive/5 border-destructive/30'}`}>
            <p className="text-sm font-medium">{result.message}</p>
            {result.remaining !== undefined && result.cap !== undefined && (
              <p className="text-xs text-muted-foreground mt-1">
                Available now: {result.remaining} of {result.cap}
                {result.plan_tier ? ` · ${result.plan_tier} plan` : ''}
              </p>
            )}
            {result.new_count !== undefined && (
              <p className="text-xs text-muted-foreground mt-1">Usage counter now: {result.new_count}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
